import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box, Card, CardMedia, Stack, Typography, Chip, IconButton, Tabs, Tab, Grid, Paper, Divider, Button, CircularProgress
} from "@mui/material";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_CLIENT_FIXED_ASSET_STATUSES, FETCH_CLIENT_FIXED_ASSET_PICTURES } from "../../constant/Apipath";
import { showErrorToast } from "../../toast/toast";
import Loader from "../../common/Loader";
import PictureViewer from "../../common/PictureViewer";
import assetPlaceholder from "../../assets/images/image-unavailable.jpg";
import { BIBlue, BIGrey, isoToMonthDateYear } from "../../assets/helpers";
import AssetQrCode from "./components/AssetQrCode";
import FixedAssetInspection from "./components/FixedAssetInspection";

// --- Helpers ---
const parseResponseList = (response) => {
  if (response?.status === 200) {
    const payload = response?.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
  }
  return [];
};

const mapAssetStatus = (item) => ({
  Id: item.id ?? item.assetStatusId,
  Location: item.location,
  LocationType: item.locationType,
  LocationTitle: item.locationTitle ?? item.LocationTitle,
  TransferDate: item.transferDate,
  StatusTypeName: item.statusTypeName ?? item.statusName,
  Comments: item.statusComments,
  SupervisorName: item.supervisorName
});

const mapAssetPicture = (pic) => ({ Id: pic.id, FileUrl: pic.fileUrl, PictureUrl: pic.pictureUrl });

const getAssetImage = (asset) => asset?.AssetImageUrl ?? asset?.ImageUrl ?? assetPlaceholder;
const getDisplayValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "NA";
};
const getStatusLabel = (status) => status?.StatusTypeName || status?.StatusName;
const getStatusColors = (label) => {
  if (!label) return { bgcolor: "#f0f0f0", color: "#333" };
  const key = label.toLowerCase().trim();
  switch (key) {
    case "active": return { bgcolor: "#d7f8e4", color: "#1b5e20" };
    case "lost": return { bgcolor: "#fdecea", color: "#b71c1c" };
    default: return { bgcolor: "#f0f0f0", color: "#333" };
  }
};

const TabPanel = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 1 }}>{children}</Box>;
};

const FixedAssetDetail = ({ selectedAsset, onBack, onTransferClick, refreshToken }) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main);
  const [activeTab, setActiveTab] = useState(0);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [assetPictures, setAssetPictures] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [pictureViewerOpen, setPictureViewerOpen] = useState(false);
  const [pictureViewerIndex, setPictureViewerIndex] = useState(0);
  const latestTransfer = assetStatuses[0];
  const currentAssetImage = assetPictures[0]?.FileUrl ?? assetPictures[0]?.PictureUrl ?? getAssetImage(selectedAsset);
  const currentLocation = latestTransfer?.LocationTitle || latestTransfer?.Location || selectedAsset?.LocationTitle || selectedAsset?.Location;
  const currentSupervisorName = latestTransfer?.SupervisorName || selectedAsset?.AssignedToName || "No Supervisor";
  const pictureViewerImages = assetPictures
    .map((pic, index) => ({
      src: pic.FileUrl || pic.PictureUrl || assetPlaceholder,
      alt: `${selectedAsset?.AssetName || "Asset"} ${index + 1}`,
    }))
    .filter((pic) => pic.src);

  const loadDetails = useCallback(async () => {
    if (!selectedAsset) return;
    setDetailLoading(true);
    const assetId = selectedAsset?.AssetId;
    const clientId = selectedClientData?.ClientId;
    const userId = loginData?.userInformation?.UserId;
    const token = loginData?.accessToken;

    try {
      const statusPayload = {
        NrOfRecPerPage: 20, PageNr: 1, FullSearch: "",
        FetchAllowedRecordsOnly: false,
        SearchList: [{ AssetId: assetId, ClientId: clientId }],
        SortList: [{ FieldName: "id", Direction: "DESC" }],
        UserId: userId
      };
      const statusRes = await Postrequestcall(FETCH_CLIENT_FIXED_ASSET_STATUSES, statusPayload, token);
      if (statusRes?.status === 200) setAssetStatuses(parseResponseList(statusRes).map(mapAssetStatus));

      const picPayload = {
        NrOfRecPerPage: 20, PageNr: 1, FullSearch: "",
        SearchList: [{ AssetId: assetId, ClientId: clientId }],
        UserId: userId
      };
      const picRes = await Postrequestcall(FETCH_CLIENT_FIXED_ASSET_PICTURES, picPayload, token);
      if (picRes?.status === 200) setAssetPictures(parseResponseList(picRes).map(mapAssetPicture));
    } catch (err) {
      showErrorToast("Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  }, [selectedAsset, selectedClientData?.ClientId, loginData?.accessToken, loginData?.userInformation?.UserId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails, refreshToken]);

  const overviewValues = [
    { label: "Category", value: getDisplayValue(selectedAsset?.CategoryName) },
    { label: "Sub Category", value: getDisplayValue(selectedAsset?.SubCategoryName) },
    { label: "Asset Code", value: selectedAsset?.AssetCode },
    { label: "Serial Number", value: selectedAsset?.SerialNo },
    { label: "Purchase Date", value: selectedAsset?.PurchaseDate ? isoToMonthDateYear(selectedAsset.PurchaseDate) : "" },
    { label: "Warranty Expiry", value: selectedAsset?.WarrantyExpireDate ? isoToMonthDateYear(selectedAsset.WarrantyExpireDate) : "" }
  ];

  return (
    <Box>
      <Button startIcon={<ArrowBackIosNewIcon />} onClick={onBack}
        sx={{ mb: 2, textTransform: "none", color: selectedClientData?.AccentColor ?? BIBlue }}>
        Back to assets
      </Button>

      {/* Header Card */}
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 14px rgba(0,0,0,0.12)", mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ p: 2,  alignItems: "center" }} >
          <CardMedia component="img" image={currentAssetImage} alt={selectedAsset?.AssetName}
            sx={{ width: 140, height: 140, borderRadius: 2, objectFit: "cover" }} />
          <Stack spacing={0.5} sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{selectedAsset?.AssetName}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>Asset Code: {getDisplayValue(selectedAsset?.AssetCode, selectedAsset?.AssetCodeName, selectedAsset?.Code)}</Typography>
            {selectedAsset?.SerialNo && <Typography variant="body2" sx={{ color: "text.secondary" }}>Serial No: {selectedAsset?.SerialNo}</Typography>}
            <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
              <LocationOnOutlinedIcon fontSize="small" sx={{ color: BIGrey }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{currentLocation || "NA"}</Typography>
            </Stack>
            <Chip label={`Supervisor: ${currentSupervisorName}`} variant="outlined"
              sx={{ mt: 1, borderColor: selectedClientData?.AccentColor ?? BIBlue, color: selectedClientData?.AccentColor ?? BIBlue, fontWeight: 600, width: "fit-content" }} />
          </Stack>
          <IconButton size="large" sx={{ borderRadius: 2, bgcolor: selectedClientData?.AccentColor ?? BIBlue, color: "#fff", alignSelf: { xs: "flex-end", sm: "center" } }} onClick={() => setQrDialogOpen(true)}>
            <QrCode2Icon />
          </IconButton>
        </Stack>
      </Card>

      <Box bgcolor="#fff" borderRadius={3} boxShadow={1} sx={{ p: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} textColor="inherit"
          sx={{ "& .MuiTabs-indicator": { backgroundColor: selectedClientData?.AccentColor ?? BIBlue, height: 3 } }}>
          {["Overview", "Transfers", "Pictures", "Inspections"].map((label, i) => (
            <Tab key={label} label={label} sx={{ color: activeTab === i ? selectedClientData?.AccentColor ?? BIBlue : "#7f8593", fontWeight: activeTab === i ? 700 : 500 }} />
          ))}
        </Tabs>
        <Divider sx={{ mt: 1 }} />

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {overviewValues.map((item) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={item.label}>
                <Paper elevation={0} sx={{
                  borderRadius: "12px", p: 1.8, minHeight: 85, bgcolor: "#fff",
                  border: "1px solid #e0e0e0", position: "relative",
                  display: "flex", flexDirection: "column", justifyContent: "center",
                  transition: "0.2s",
                  "&:hover": { boxShadow: "0 6px 14px rgba(0,0,0,0.08)" },
                  "&::before": {
                    content: '""', position: "absolute", top: 0, left: 0, width: "100%", height: "4px",
                    background: selectedClientData?.AccentColor ?? "#1976d2",
                    borderTopLeftRadius: "12px", borderTopRightRadius: "12px"
                  }
                }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", letterSpacing: "0.5px" }}>
                    {item.label.toUpperCase()}
                  </Typography>
                  <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1f2937", mt: 0.6 }}>
                    {item.value || "-"}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
            <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Transfer History</Typography>
              {selectedClientData?.IsAdmin === 1 && (
                <Button
                  variant="contained"
                  onClick={onTransferClick}
                  sx={{
                    backgroundColor: selectedClientData?.AccentColor ?? BIBlue,
                    color: "#fff",
                    boxShadow: "none"
                  }}>
                  Transfer Asset
                </Button>
              )}
            </Stack>
          {detailLoading ? <Loader /> : (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {assetStatuses.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No transfer records yet.</Typography>
              ) : (
                assetStatuses.map((history) => {
                  const statusLabel = getStatusLabel(history);
                  const style = getStatusColors(statusLabel);
                  return (
                    <Paper key={history.Id} variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{history.LocationTitle || history.Location}</Typography>
                        <Typography variant="caption" sx={{ display: "block",  color: "text.secondary" }} >
                          Type: {history.LocationType || "N/A"} | Date: {moment(history.TransferDate).format("MMM DD, YYYY")}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Supervisor: {history.SupervisorName || "N/A"}</Typography>
                      </Box>
                      <Chip size="small" label={statusLabel} sx={{ ...style, fontWeight: 600 }} />
                    </Paper>
                  );
                })
              )}
            </Stack>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {detailLoading ? <Loader /> : (
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {assetPictures.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No pictures uploaded yet.</Typography>
              ) : (
                assetPictures.map((pic, i) => (
                  <Grid item xs={6} sm={4} key={pic.Id || i}>
                    <Paper
                      onClick={() => {
                        setPictureViewerIndex(i);
                        setPictureViewerOpen(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        minHeight: 140,
                        cursor: "pointer",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
                        },
                      }}>
                      <Box
                        component="img"
                        src={pic.FileUrl || pic.PictureUrl || assetPlaceholder}
                        sx={{ width: "100%", height: 140, objectFit: "cover" }}
                      />
                    </Paper>
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <FixedAssetInspection
            selectedAsset={selectedAsset}
            refreshToken={refreshToken}
          />
        </TabPanel>
      </Box>

      {pictureViewerOpen && (
        <PictureViewer
          isOpen={pictureViewerOpen}
          onClose={() => setPictureViewerOpen(false)}
          pictures={pictureViewerImages}
          viewerIndex={pictureViewerIndex}
        />
      )}

      {qrDialogOpen && (
        <AssetQrCode qrDialogOpen={qrDialogOpen} setQrDialogOpen={setQrDialogOpen} selectedAsset={selectedAsset} />
      )}
    </Box>
  );
};

export default FixedAssetDetail;
